// StateScreens — the non-ready views.
//
// Loading      : pre-resolve skeleton card.
// Submitted    : thank-you receipt.
// Already      : someone already answered.
// Expired      : 48h link window elapsed.
// Superseded   : a newer link already covered these bookings (cascade).
// AllCaughtUp  : contact has nothing in-window to confirm right now.
// NotFound     : bad / missing token.
// Cancelled    : booking was cancelled after the SMS went out.
// Error        : network or 5xx.

import type { ComponentType } from 'react';
import type { BookingView } from '../types';
import { CheckIcon, CrossIcon, InfoIcon } from './Icons';

type IconLike = ComponentType<{ width?: string | number; height?: string | number }>;

type Tone = 'ok' | 'warn' | 'no';

interface ReceiptProps {
  tone: Tone;
  title: string;
  body: string;
  Icon?: IconLike;
}

function toneClass(tone: Tone): string {
  if (tone === 'ok') return '';
  if (tone === 'no') return 'no';
  return 'warn';
}

function Receipt({ tone, title, body, Icon }: ReceiptProps) {
  const cls = toneClass(tone);
  const Render = Icon ?? (tone === 'ok' ? CheckIcon : InfoIcon);
  return (
    <div className={`receipt ${cls}`.trim()}>
      <div className="receipt-inner">
        <div className={`tick ${cls}`.trim()} aria-hidden="true">
          <Render />
        </div>
        <h2>{title}</h2>
        <p>{body}</p>
      </div>
    </div>
  );
}

export function LoadingScreen() {
  return (
    <div className="card" aria-busy="true" aria-label="Loading">
      <div className="greet">
        <div className="sk" style={{ width: '80%', height: 22, marginBottom: 10 }}></div>
        <div className="sk" style={{ width: '60%', height: 14 }}></div>
      </div>
      <div className="dateband">
        <div className="sk" style={{ width: 70, height: 46 }}></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="sk" style={{ width: 90, height: 12 }}></div>
          <div className="sk" style={{ width: 140, height: 18 }}></div>
        </div>
      </div>
      <div className="visits">
        <div className="sk" style={{ width: '100%', height: 60, marginBottom: 10 }}></div>
        <div className="sk" style={{ width: '100%', height: 60 }}></div>
      </div>
    </div>
  );
}

export interface SubmittedEntry {
  booking: BookingView;
  action: 'confirmed' | 'did_not_attend';
}

export interface SubmittedScreenProps {
  /** One entry per submitted booking in the bundle. Order matches the order
   *  they appeared on the BookingCard. The receipt renders one summary line
   *  per entry with that entry's individual outcome — critical for the
   *  multi-visit case so a booking marked 'happened' isn't shown labelled
   *  'didn't happen' just because a sibling in the same bundle was a no. */
  entries: SubmittedEntry[];
}

export function SubmittedScreen({ entries }: SubmittedScreenProps) {
  // Header tone/title/body reflect whether the whole bundle was clean.
  // If any single booking in the bundle was a no, the follow-up call
  // message is the right framing for the receipt overall — but pluralise
  // when more than one visit was a no, so the copy matches what the
  // veteran actually answered.
  const didNotAttendCount = entries.filter((e) => e.action === 'did_not_attend').length;
  const anyDidNotAttend = didNotAttendCount > 0;
  const headerTone: Tone = anyDidNotAttend ? 'warn' : 'ok';
  const headerCls = toneClass(headerTone);
  const title = anyDidNotAttend ? "Thanks — we've got it." : 'Thank you — all recorded.';
  let body: string;
  if (!anyDidNotAttend) {
    body = 'You can close this page. Nothing else is needed from you.';
  } else if (didNotAttendCount === 1) {
    body = "A member of our team will call you within one business day about the visit that didn't go to plan. You don't need to do anything else.";
  } else {
    body = `A member of our team will call you within one business day about the ${didNotAttendCount} visits that didn't go to plan. You don't need to do anything else.`;
  }

  return (
    <div className={`receipt ${headerCls}`.trim()}>
      <div className="receipt-inner">
        <div className={`tick ${headerCls}`.trim()} aria-hidden="true">
          {anyDidNotAttend ? <InfoIcon /> : <CheckIcon />}
        </div>
        <h2>{title}</h2>
        <p>{body}</p>
        {entries.length > 0 && (
          <div className="summary">
            {entries.map((e) => {
              const ok = e.action !== 'did_not_attend';
              return (
                <div key={e.booking.id} className={`line ${ok ? 'ok' : 'bad'}`}>
                  {ok ? (
                    <CheckIcon width="16" height="16" />
                  ) : (
                    <CrossIcon width="16" height="16" />
                  )}
                  <span>{e.booking.service_type}</span>
                  <span className="ty">{ok ? 'Happened' : "Didn't happen"}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function AlreadyDoneScreen() {
  return (
    <Receipt
      tone="warn"
      title="This has already been answered."
      body="Thanks — someone has already responded. Nothing more to do. If that wasn't you, open the chat below."
    />
  );
}

export function ExpiredScreen() {
  return (
    <Receipt
      tone="warn"
      title="This link has expired."
      body="Confirmation links work for 48 hours. Your records aren't affected — our team can still help by chat."
    />
  );
}

export function SupersededScreen() {
  return (
    <Receipt
      tone="ok"
      title="You're all up to date."
      body="The visits this link covered have already been confirmed on a more recent text. Nothing more to do."
    />
  );
}

export function AllCaughtUpScreen() {
  return (
    <Receipt
      tone="ok"
      title="You're all up to date."
      body="There are no recent visits waiting for your confirmation right now. If a new one comes through later, we'll text you a fresh link."
    />
  );
}

export function NotFoundScreen() {
  return (
    <Receipt
      tone="warn"
      title="We couldn't find this page."
      body="The link may have been copied incompletely. Open the chat below and we'll help."
    />
  );
}

export function CancelledScreen() {
  return (
    <Receipt
      tone="warn"
      title="Your visit was cancelled."
      body="This visit was cancelled after your text message went out, so there's nothing to confirm."
    />
  );
}

export function ErrorScreen() {
  return (
    <Receipt
      tone="warn"
      title="Something went wrong."
      body="Please try again in a few minutes. If it keeps happening, open the chat below."
    />
  );
}
